import { z } from "zod";
import { schemas } from "../generated/serverClient";

export const TheoryLectureCategoryEnum = schemas.TheoryLectureCategoryEnum;
export type TheoryLectureCategory = z.infer<typeof TheoryLectureCategoryEnum>;

export type TheoryLecture = z.infer<typeof schemas.TheoryLectureDto>;

export type TheoryLectureAbbreviation =
  | "G1"
  | "G2"
  | "G3"
  | "G4"
  | "G5"
  | "G6"
  | "G7"
  | "G8"
  | "G9"
  | "G10"
  | "G11"
  | "G12"
  | "A1"
  | "A2"
  | "A3"
  | "A4"
  | "B1"
  | "B2"
  | "B96"
  | "L1"
  | "L2"
  | "T1"
  | "T2"
  | "T3"
  | "T4"
  | "T5"
  | "T6";

export function theoryUnitToTheoryLecture(theoryUnit: number): TheoryLecture {
  if (theoryUnit >= 1 && theoryUnit <= 12) {
    return { category: "Grundstoff", lectureNumber: theoryUnit };
  } else if (theoryUnit >= 13 && theoryUnit <= 14) {
    return { category: "Zusatzstoff_B", lectureNumber: theoryUnit - 12 };
  } else if (theoryUnit >= 15 && theoryUnit <= 18) {
    return { category: "Zusatzstoff_A", lectureNumber: theoryUnit - 14 };
  } else if (theoryUnit === 96) {
    return { category: "B96", lectureNumber: 1 };
  } else {
    throw new Error(`Invalid theoryUnit: ${JSON.stringify(theoryUnit)}`);
  }
}

export function theoryLectureAbbreviation(theoryLecture: TheoryLecture): TheoryLectureAbbreviation {
  if (theoryLecture.category === "Grundstoff") {
    return `G${theoryLecture.lectureNumber}` as TheoryLectureAbbreviation;
  } else if (theoryLecture.category === "B96") {
    return "B96";
  } else {
    const m = /Zusatzstoff_(.*)/.exec(theoryLecture.category);
    if (!m) {
      throw new Error(`Invalid theoryLecture: ${JSON.stringify(theoryLecture.category)}`);
    }
    const prefix = m[1];
    return `${prefix}${theoryLecture.lectureNumber}` as TheoryLectureAbbreviation;
  }
}

export function theoryLectureFromAbbreviation(abbreviation: string): TheoryLecture {
  const lectureNumber = parseInt(abbreviation.substring(1), 10);
  if (abbreviation.startsWith("G") && lectureNumber >= 1 && lectureNumber <= 12) {
    return { category: "Grundstoff", lectureNumber };
  } else if (abbreviation.startsWith("A") && lectureNumber >= 1 && lectureNumber <= 4) {
    return { category: "Zusatzstoff_A", lectureNumber };
  } else if (abbreviation.startsWith("B") && lectureNumber >= 1 && lectureNumber <= 2) {
    return { category: "Zusatzstoff_B", lectureNumber };
  } else if (abbreviation === "B96") {
    return { category: "B96", lectureNumber: 1 };
  } else if (abbreviation.startsWith("L") && lectureNumber >= 1 && lectureNumber <= 2) {
    return { category: "Zusatzstoff_L", lectureNumber };
  } else if (abbreviation.startsWith("T") && lectureNumber >= 1 && lectureNumber <= 6) {
    return { category: "Zusatzstoff_T", lectureNumber };
  } else {
    throw new Error(`Invalid abbreviation: ${JSON.stringify(abbreviation)}`);
  }
}

const _categoryOrder = Object.fromEntries(
  TheoryLectureCategoryEnum.options.map((category, index) => [category, index] as const),
);

export function sortTheoryLectureCategories(categories: Array<TheoryLectureCategory>): Array<TheoryLectureCategory> {
  return categories.sort((a, b) => _categoryOrder[a] - _categoryOrder[b]);
}
